// =============================================================
// SCREENS 2 — Champions / Détail champion / Builder — données réelles
// =============================================================
import React, { useState, useMemo, useEffect } from 'react';
import {
  Icon, SecHead, ChampImg, ItemImg, SpellById, Meter, Kpi, LineChart, ChampSelector,
  Spinner, ErrorState, EmptyState,
} from './components';
import {
  champSplash, champSquare, champLoading, getChamp, ddVersion,
  getItem, getRuneStyle, getRunePerk, getStatShard, runeStyleIcon, imgPath, fetchChampionFull,
} from './ddragon';
import { useApi } from './useApi';
import { btrApi } from './api';
import * as A from './adapters';
import { WINDOW_DAYS, LANE_TO_POSITION, laneLabel, classFr, matchesQuery } from './format';

// ============================================================
// CHAMPIONS LIST
// ============================================================
function ChampionsScreen({ setRoute }) {
  const [q, setQ] = useState("");
  const [role, setRole] = useState("Tous");
  const [tag, setTag] = useState("Tous");
  const [view, setView] = useState("grid");
  const [sort, setSort] = useState("tier");
  const PAGE = 48;
  const [shown, setShown] = useState(PAGE);
  useEffect(() => { setShown(PAGE); }, [q, role, tag, sort, view]);

  const stats = useApi('home-stats', () => btrApi.siteStats());
  const champsState = useApi('champ-list', () => btrApi.champions({ days: WINDOW_DAYS, limit: 200, sort: 'games' }));
  const posState = useApi('champ-pos', () => btrApi.championPositions({ days: WINDOW_DAYS }));

  const roles = ["Tous","Top","Jungle","Mid","ADC","Support"];
  const tags = ["Tous","Fighter","Tank","Mage","Assassin","Marksman","Support"];

  const all = useMemo(
    () => A.adaptChampions(champsState.data || [], posState.data || {}, stats.data?.matches || 0),
    [champsState.data, posState.data, stats.data],
  );

  const list = useMemo(() => {
    let l = all.filter(c =>
      (matchesQuery(c.name, q) || matchesQuery(c.id, q)) &&
      (role === "Tous" || c.roles.includes(role)) &&
      (tag === "Tous" || c.tags.includes(tag))
    );
    if (sort === "tier") l = [...l].sort((a,b) => "SABCD".indexOf(a.tier) - "SABCD".indexOf(b.tier) || b.games - a.games);
    if (sort === "winrate") l = [...l].sort((a,b) => b.winrate - a.winrate);
    if (sort === "pickrate") l = [...l].sort((a,b) => b.pickrate - a.pickrate);
    if (sort === "games") l = [...l].sort((a,b) => b.games - a.games);
    if (sort === "name") l = [...l].sort((a,b) => a.name.localeCompare(b.name));
    return l;
  }, [all, q, role, tag, sort]);

  const loading = champsState.loading && !all.length;

  return (
    <div className="container anim-fade-in">
      <header style={{marginBottom: 36}}>
        <div className="eyebrow">Annuaire des champions · Patch {ddVersion()}</div>
        <h1 className="h1" style={{marginTop:10}}>
          {all.length || "…"} champions, <span style={{color:"var(--accent)"}}>une seule recherche</span>
        </h1>
        <p className="lead" style={{marginTop:14, maxWidth: 680}}>
          Triez, filtrez, comparez. Statistiques agrégées sur les {WINDOW_DAYS} derniers jours de parties analysées.
        </p>
      </header>

      <div className="card" style={{marginBottom: 24, padding: 20}}>
        <div className="row" style={{gap:14,flexWrap:"wrap"}}>
          <div className="field" style={{flex:"1 1 240px",maxWidth:360}}>
            <Icon name="search" size={15} style={{color:"var(--ink-3)"}}/>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Chercher un champion…" aria-label="Chercher un champion"/>
          </div>
          <div className="row-tight" style={{gap:8}}>
            <span className="muted" style={{fontSize:12,fontWeight:600}}>Rôle</span>
            <div className="seg">
              {roles.map(r => <button key={r} className={role === r ? "active" : ""} onClick={() => setRole(r)}>{r}</button>)}
            </div>
          </div>
          <div className="grow"/>
          <div className="field" style={{padding:0}}>
            <select value={sort} onChange={e => setSort(e.target.value)} style={{padding:"9px 14px"}}>
              <option value="tier">Tier list</option>
              <option value="winrate">Winrate ↓</option>
              <option value="pickrate">Pickrate ↓</option>
              <option value="games">Popularité ↓</option>
              <option value="name">Nom A→Z</option>
            </select>
          </div>
          <div className="seg">
            <button className={view==="grid" ? "active" : ""} onClick={() => setView("grid")}>Galerie</button>
            <button className={view==="table" ? "active" : ""} onClick={() => setView("table")}>Tableau</button>
          </div>
        </div>
        <div className="row" style={{gap:8,marginTop:14,flexWrap:"wrap",alignItems:"center"}}>
          <span className="muted" style={{fontSize:12,fontWeight:600,marginRight:4}}>Classe</span>
          {tags.map(t => (
            <button key={t} className={"tag " + (tag === t ? "accent" : "")} onClick={() => setTag(t)}
                    style={{cursor:"pointer", padding:"5px 12px",fontSize:12}}>{t === "Tous" ? "Toutes" : classFr(t)}</button>
          ))}
        </div>
      </div>

      {loading ? <Spinner label="Chargement des champions…"/> :
       champsState.error ? <ErrorState error={champsState.error} onRetry={champsState.refetch}/> :
       !list.length ? <EmptyState title="Aucun champion" hint="Aucun résultat pour ces filtres, ou données à recollecter."/> : (
      <>
        <div className="muted" style={{fontSize:13,marginBottom:16}}>{list.length} résultat{list.length > 1 ? "s" : ""}{shown < list.length ? ` · ${Math.min(shown, list.length)} affichés` : ""}</div>
        {view === "grid" ? (
          <div className="champ-grid stagger">
            {list.slice(0, shown).map(c => (
              <button key={c.id} className="poster" onClick={() => setRoute("champion", { champId: c.id })}>
                <div className="poster-img">
                  <img src={champLoading(c.id)} alt={c.name} loading="lazy" decoding="async"
                       onError={e => e.target.src = champSquare(c.id)}/>
                </div>
                <div className="poster-tier"><span className={`tier tier-${c.tier}`}>{c.tier}</span></div>
                <div className="poster-info">
                  <div className="poster-name">{c.name}</div>
                  <div className="poster-title">{c.title}</div>
                  <div className="row" style={{gap:6,marginTop:8}}>
                    <span className="tag" style={{background:"rgba(255,255,255,0.16)",color:"#fff",fontSize:10,fontWeight:700}}>WR {c.winrate}%</span>
                    <span className="tag" style={{background:"rgba(255,255,255,0.10)",color:"rgba(255,255,255,0.85)",fontSize:10}}>PR {c.pickrate}%</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="card card-flush">
            <table className="tbl">
              <thead><tr><th style={{width:32}}>#</th><th>Champion</th><th>Rôle</th><th>Tier</th><th>Winrate</th><th className="num">Pickrate</th><th className="num">Parties</th><th>Difficulté</th></tr></thead>
              <tbody>
                {list.slice(0, shown).map((c, i) => (
                  <tr key={c.id} style={{cursor:"pointer"}} onClick={() => setRoute("champion", { champId: c.id })}>
                    <td className="muted">{i+1}</td>
                    <td><div className="row-tight"><ChampImg id={c.id} size="sm"/><div><div style={{fontWeight:700,fontSize:14}}>{c.name}</div><div className="muted" style={{fontSize:11,fontStyle:"italic"}}>{c.title}</div></div></div></td>
                    <td>{c.roles.map(r => <span key={r} className="tag" style={{marginRight:4}}>{r}</span>)}</td>
                    <td><span className={`tier tier-${c.tier}`}>{c.tier}</span></td>
                    <td style={{minWidth:150}}>
                      <div className="row-bw" style={{marginBottom:4}}><span className="mono" style={{color:c.winrate>50?"var(--win)":"var(--loss)",fontWeight:700,fontSize:14}}>{c.winrate}%</span></div>
                      <Meter value={(c.winrate-40)*5} kind={c.winrate>50?"win":"loss"}/>
                    </td>
                    <td className="num mono">{c.pickrate}%</td>
                    <td className="num mono">{c.games}</td>
                    <td><div className="row-tight" style={{gap:2}}>{Array.from({length:10}).map((_,k) => <span key={k} style={{width:5,height:14,borderRadius:1,background: k < c.difficulty ? "var(--accent)" : "var(--surface-3)"}}/>)}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {shown < list.length && (
          <div style={{textAlign:"center",marginTop:24}}>
            <button className="btn outline lg" onClick={() => setShown(n => n + PAGE)}>
              Afficher plus ({list.length - shown} restants)
            </button>
          </div>
        )}
      </>)}
    </div>
  );
}

// ============================================================
// CHAMPION DETAIL
// ============================================================
function ChampionDetailScreen({ setRoute, nav }) {
  const champId = nav?.champId || "Ahri";
  const dd = getChamp(champId);
  const key = dd?.key;
  const [tab, setTab] = useState("overview");

  const stats = useApi('home-stats', () => btrApi.siteStats());
  const detail = useApi(key ? `champ-${key}` : null, () => btrApi.champion(key, { days: WINDOW_DAYS }));
  const c = detail.data ? A.adaptChampionDetail(detail.data, stats.data?.matches || 0)
                        : (dd ? { id: dd.id, name: dd.name, title: dd.title, tags: dd.tags, roles: [], difficulty: dd.info?.difficulty ?? 5, winrate: 0, pickrate: 0, games: 0, kda: 0, kills: 0, deaths: 0, tier: 'C', byPosition: {} } : null);

  if (!dd || !c) return <div className="container"><EmptyState title="Champion introuvable"/></div>;

  return (
    <div className="anim-fade-in">
      <div className="container container-fluid" style={{padding: "20px 28px 0"}}>
        <button className="btn ghost sm" onClick={() => setRoute("champions")} style={{marginBottom:14}}><Icon name="arrowL" size={14}/> Retour aux champions</button>
        <section className="hero" style={{minHeight: 500, padding: 40}}>
          <div className="hero-bg"><img src={champSplash(c.id, 0)} alt={c.name}/></div>
          <div className="hero-content" style={{maxWidth: 720}}>
            <div className="row" style={{gap:8,marginBottom:18}}>
              <span className={`tier tier-${c.tier}`} style={{width:30,height:30,fontSize:15}}>{c.tier}</span>
              {c.roles.map(r => <span key={r} className="tag accent">{r}</span>)}
              {c.tags.slice(0,2).map(t => <span key={t} className="tag" style={{background:"rgba(255,255,255,0.14)",color:"#fff"}}>{t}</span>)}
            </div>
            <h1 className="h1" style={{color:"#fff", fontSize:"clamp(56px,9vw,108px)", lineHeight: 0.9}}>{c.name}</h1>
            <p style={{color:"rgba(255,255,255,0.78)",fontSize:22,marginTop:8,fontStyle:"italic",fontWeight:300}}>« {c.title} »</p>
            <div className="row" style={{marginTop:32,gap:10}}>
              <button className="btn primary lg" onClick={() => setRoute("build", { champId: c.id })}><Icon name="layers" size={16}/> Voir le build</button>
            </div>
          </div>
        </section>
      </div>

      <div className="container">
        <div className="g-4 stagger kpis" style={{marginTop:-40,position:"relative",zIndex:5,marginBottom:40}}>
          <Kpi label="Winrate" value={c.winrate ? c.winrate + "%" : "—"}
               trend={c.winrate ? `${c.winrate >= 50 ? "+" : ""}${(c.winrate-50).toFixed(1)} pt vs la moyenne` : null} trendDir={c.winrate>50?"up":"down"}/>
          <Kpi label="Pickrate" value={c.pickrate ? c.pickrate + "%" : "—"}/>
          <Kpi label="Parties" value={c.games ? c.games.toLocaleString("fr-FR") : "—"}/>
          <Kpi label="Difficulté" value={c.difficulty + "/10"} accent/>
        </div>

        <nav className="tabs">
          {[
            { id:"overview", label:"Vue d'ensemble" },
            { id:"build", label:"Build conseillé" },
            { id:"matchups", label:"Matchups" },
            { id:"abilities", label:"Sorts" },
          ].map(t => <button key={t.id} className={tab === t.id ? "active" : ""} onClick={() => setTab(t.id)}>{t.label}</button>)}
        </nav>

        <div key={tab} className="anim-fade-in">
          {tab === "overview"  && <ChampOverview c={c}/>}
          {tab === "build"     && <BuildSheet champ={c.id} role={c.roles[0]}/>}
          {tab === "matchups"  && <ChampMatchups championKey={key}/>}
          {tab === "abilities" && <ChampAbilities c={c}/>}
        </div>
      </div>
    </div>
  );
}

function ChampOverview({ c }) {
  const [full, setFull] = useState(null);
  useEffect(() => { let on = true; fetchChampionFull(c.id).then(f => on && setFull(f)).catch(() => {}); return () => { on = false; }; }, [c.id]);
  const info = full?.info || {};
  const stats = [
    { label:"Attaque", v: info.attack ?? 5 },
    { label:"Défense", v: info.defense ?? 5 },
    { label:"Magie", v: info.magic ?? 5 },
    { label:"Difficulté", v: info.difficulty ?? c.difficulty },
  ];
  const byPos = c.byPosition || {};
  const trend = useApi(c.championId ? `trend-${c.championId}` : null, () => btrApi.championTrend(c.championId, { days: 365 }));
  // La semaine en cours est incomplète : on ne l'affiche pas (elle faisait un faux « pic »).
  const trendPts = (() => {
    const pts = trend.data || [];
    if (pts.length >= 2 && Number(pts[pts.length-1].games) < Number(pts[pts.length-2].games) * 0.5) return pts.slice(0, -1);
    return pts;
  })();
  const trendVals = trendPts.map(p => Number(p.winrate));
  const fmtWeek = (iso) => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  return (
    <div className="g-2" style={{alignItems:"start"}}>
      <div className="stack">
        <div className="card">
          <SecHead title="L'histoire" meta="Lore officiel"/>
          <p className="body" style={{fontSize:15,lineHeight:1.7,color:"var(--ink-2)"}}>{full?.lore || full?.blurb || "Chargement du lore…"}</p>
        </div>
        <div className="card">
          <SecHead title="Tendance winrate" meta={`par semaine · ${trendPts.length || ''} dernières semaines`}
                   action={trendVals.length ? <span className={"tag " + (trendVals[trendVals.length-1] >= 50 ? "win" : "loss")}>{trendVals[trendVals.length-1]}%</span> : null}/>
          {trend.loading && !trend.data ? <Spinner/> :
           trendVals.length > 1 ? <LineChart values={trendVals} accent={trendVals[trendVals.length-1] >= 50 ? "win" : "loss"} height={220}
                                             startLabel={fmtWeek(trendPts[0].period)} endLabel={`Semaine du ${fmtWeek(trendPts[trendPts.length-1].period)}`}/> :
           <EmptyState title="Pas assez d'historique" hint="La courbe se remplit à mesure de la collecte."/>}
        </div>
        <div className="card">
          <SecHead title="Performance par rôle" meta={`${WINDOW_DAYS} derniers jours`}/>
          {Object.keys(byPos).length === 0 ? <EmptyState title="Pas de données par rôle"/> : (
            <div className="stack-sm">
              {Object.entries(byPos).sort((a,b)=>(b[1]?.games||0)-(a[1]?.games||0)).map(([pos, d]) => (
                <div key={pos}>
                  <div className="row-bw" style={{fontSize:13,marginBottom:5}}>
                    <span style={{fontWeight:600}}>{laneLabel(pos)}</span>
                    <span className="mono" style={{color:(d?.winrate||0)>50?"var(--win)":"var(--loss)",fontWeight:600}}>{Number(d?.winrate||0).toFixed(1)}% · {d?.games} parties</span>
                  </div>
                  <Meter value={(Number(d?.winrate||0)-40)*5} kind={(d?.winrate||0)>50?"win":"loss"}/>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="stack">
        <div className="card">
          <SecHead title="Caractéristiques"/>
          {stats.map(s => (
            <div key={s.label} style={{marginBottom:12}}>
              <div className="row-bw" style={{fontSize:13,marginBottom:5}}><span style={{fontWeight:600}}>{s.label}</span><span className="mono t-accent" style={{fontWeight:700}}>{s.v}/10</span></div>
              <Meter value={s.v*10}/>
            </div>
          ))}
        </div>
        <div className="card">
          <SecHead title="Statistiques agrégées"/>
          <div className="g-2" style={{gap:16}}>
            <div><div className="bignum" style={{fontSize:28,color: c.winrate>50?"var(--win)":"var(--loss)"}}>{c.winrate || "—"}%</div><div className="muted" style={{fontSize:12}}>Winrate</div></div>
            <div><div className="bignum" style={{fontSize:28}}>{c.kda || "—"}</div><div className="muted" style={{fontSize:12}}>KDA moyen</div></div>
            <div><div className="bignum" style={{fontSize:28}}>{(c.kills||0).toFixed(1)}</div><div className="muted" style={{fontSize:12}}>Kills moy.</div></div>
            <div><div className="bignum" style={{fontSize:28}}>{(c.deaths||0).toFixed(1)}</div><div className="muted" style={{fontSize:12}}>Morts moy.</div></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChampMatchups({ championKey }) {
  const mu = useApi(championKey ? `mu-${championKey}` : null, () => btrApi.matchups({ champion1: championKey, days: WINDOW_DAYS }));
  const rows = (mu.data || []).map(A.adaptMatchup).filter(m => m.games >= 5);
  const seen = new Set();
  const uniq = rows.filter(m => { if (seen.has(m.id)) return false; seen.add(m.id); return true; });
  const sorted = [...uniq].sort((a,b) => a.winrate - b.winrate);
  const hard = sorted.slice(0, 5);
  const easy = [...sorted].reverse().slice(0, 5);

  if (mu.loading && !mu.data) return <Spinner/>;
  if (!rows.length) return <EmptyState title="Pas de matchup" hint="Données insuffisantes pour ce champion."/>;

  return (
    <div className="g-2">
      <div className="card">
        <SecHead title="Difficile contre" meta="Plus faibles winrates"/>
        <div className="stack-sm">{hard.map(m => <MUrow key={m.id} champ={m} wr={m.winrate} games={m.games} bad/>)}</div>
      </div>
      <div className="card">
        <SecHead title="Facile contre" meta="Meilleurs winrates"/>
        <div className="stack-sm">{easy.map(m => <MUrow key={m.id} champ={m} wr={m.winrate} games={m.games}/>)}</div>
      </div>
    </div>
  );
}

function MUrow({ champ, wr, games, bad }) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:14,padding:"10px 8px",borderRadius:10}}>
      <ChampImg id={champ.id} size="sm"/>
      <div className="grow"><div style={{fontWeight:700,fontSize:14}}>{champ.name}</div><div className="muted" style={{fontSize:11,fontStyle:"italic"}}>{champ.title}</div></div>
      <div style={{textAlign:"right"}}>
        <div className="mono" style={{color: bad ? "var(--loss)" : "var(--win)",fontWeight:700,fontSize:15}}>{Number(wr).toFixed(1)}%</div>
        <div className="muted" style={{fontSize:11}}>{games.toLocaleString("fr-FR")} parties</div>
      </div>
    </div>
  );
}

function ChampAbilities({ c }) {
  const [full, setFull] = useState(null);
  useEffect(() => { let on = true; fetchChampionFull(c.id).then(f => on && setFull(f)).catch(() => {}); return () => { on = false; }; }, [c.id]);
  if (!full) return <Spinner/>;
  const abilities = [
    { key:"P", name: full.passive?.name, img: imgPath('passive', full.passive?.image?.full), body: full.passive?.description },
    ...["Q","W","E","R"].map((k,i) => ({ key:k, name: full.spells?.[i]?.name, img: imgPath('spell', full.spells?.[i]?.image?.full), body: full.spells?.[i]?.description })),
  ];
  return (
    <div className="stack">
      {abilities.map((a) => (
        <div key={a.key} className="card" style={{display:"grid",gridTemplateColumns:"auto 1fr",gap:20,alignItems:"center"}}>
          <div style={{position:"relative",flexShrink:0}}>
            <div className="item" style={{width:72,height:72,border:"2px solid var(--accent)",borderRadius:14}}>
              <img src={a.img} alt={a.name} onError={e => e.target.src = champSquare(c.id)}/>
            </div>
            <div style={{position:"absolute",top:-8,left:-8,width:26,height:26,borderRadius:8,background:"var(--accent)",color:"var(--accent-text)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"var(--font-display)",fontWeight:800,fontSize:13,boxShadow:"0 4px 12px var(--accent-glow)"}}>{a.key}</div>
          </div>
          <div>
            <h3 className="h4" style={{fontSize:20}}>{a.name}</h3>
            <p className="body" style={{margin:"6px 0 0",fontSize:14}} dangerouslySetInnerHTML={{__html: a.body || ""}}/>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// BUILD — Builder
// ============================================================
// Couleurs par sort pour l'ordre de montée (R = accent)
const SKILL_COLORS = { Q: "#4f8df9", W: "#34d399", E: "#f59e0b", R: "var(--accent)" };

function SkillOrderStrip({ order }) {
  // Priorité de max : fréquence dans les 9 premiers niveaux, R d'abord
  const counts = {};
  order.slice(0, 9).forEach((s) => { if (s !== "R") counts[s] = (counts[s] || 0) + 1; });
  const priority = Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([s]) => s);
  return (
    <div>
      <div className="row" style={{gap:8,alignItems:"center",marginBottom:14,flexWrap:"wrap"}}>
        <span className="muted" style={{fontSize:12}}>Priorité</span>
        {["R", ...priority].map((s, i) => (
          <React.Fragment key={s}>
            {i > 0 && <span className="muted">›</span>}
            <span className="mono" style={{fontWeight:800,fontSize:16,color:SKILL_COLORS[s]}}>{s}</span>
          </React.Fragment>
        ))}
      </div>
      <div className="row" style={{gap:6,flexWrap:"wrap"}}>
        {order.map((s, i) => (
          <div key={i} style={{textAlign:"center"}}>
            <div className="mono" style={{
              width:30,height:30,lineHeight:"30px",borderRadius:8,fontWeight:800,fontSize:13,
              background:"var(--surface-2)",color:SKILL_COLORS[s],
              border:s==="R"?"1px solid var(--accent)":"1px solid transparent",
            }}>{s}</div>
            <div className="muted" style={{fontSize:9,marginTop:3}}>{i+1}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BuildScreen({ setRoute, nav }) {
  const [champ, setChamp] = useState(nav?.champId || "Ahri");
  const [enemy, setEnemy] = useState("");
  const [role, setRole] = useState("Mid");
  return (
    <div className="container anim-fade-in">
      <header style={{marginBottom: 32}}>
        <div className="eyebrow">Builds · Compose ta recette</div>
        <h1 className="h1" style={{marginTop:10}}>Le build qui <span style={{color:"var(--accent)"}}>gagne</span>, en quelques clics.</h1>
        <p className="lead" style={{marginTop:14, maxWidth: 680}}>
          Choisis ton champion, ton rôle et, si tu veux, ton adversaire de lane. On agrège les
          objets, runes et sorts des parties classées des {WINDOW_DAYS} derniers jours.
        </p>
      </header>

      <div className="card" style={{padding: 24, marginBottom: 28}}>
        <div className="picker">
          <div><span className="field-label">Mon champion</span><ChampSelector value={champ} onChange={setChamp}/></div>
          <div>
            <span className="field-label">Rôle</span>
            <div className="seg" style={{width:"100%"}}>
              {["Top","Jungle","Mid","ADC","Support"].map(r => <button key={r} className={role === r ? "active" : ""} onClick={() => setRole(r)} style={{flex:1}}>{r}</button>)}
            </div>
          </div>
          <div><span className="field-label">Adversaire de lane (optionnel)</span><ChampSelector value={enemy} onChange={setEnemy} placeholder="Aucun"/></div>
        </div>
        <style>{`.picker { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; align-items: end; } @media (max-width: 900px) { .picker { grid-template-columns: 1fr; } }`}</style>
      </div>

      <BuildSheet champ={champ} role={role} vs={enemy}/>
    </div>
  );
}

function BuildSheet({ champ, role, vs }) {
  const dd = getChamp(champ);
  const position = role ? LANE_TO_POSITION[role] : null;
  const vsName = vs ? (getChamp(vs)?.id) : null;
  const params = { champion: dd?.id || champ, role: position || undefined, vs: vsName || undefined, days: WINDOW_DAYS };
  const key = `build-${params.champion}-${params.role || 'any'}-${params.vs || 'any'}`;
  const state = useApi(dd ? key : null, () => btrApi.build(params));

  if (!dd) return <EmptyState title="Champion inconnu"/>;
  if (state.loading && !state.data) return <Spinner label="Calcul du build…"/>;
  if (state.error) return <ErrorState error={state.error} label="Pas assez de données pour ce build" onRetry={state.refetch}/>;
  const b = state.data;
  if (!b) return <EmptyState title="Aucun build" hint="Essaie sans adversaire ou un autre rôle."/>;

  const primary = b.runes && getRuneStyle(b.runes.primaryStyle);
  const sub = b.runes && getRuneStyle(b.runes.subStyle);

  return (
    <div>
      <div className="card" style={{marginBottom: 24, padding: 24}}>
        <div className="row" style={{gap:20,flexWrap:"wrap"}}>
          <ChampImg id={dd.id} size="xl" glow/>
          <div className="grow">
            <div className="eyebrow">Recette agrégée</div>
            <h2 className="h2" style={{marginTop:6,fontSize:32}}>Build {dd.name}{role ? ` · ${role}` : ""}{vsName ? ` vs ${getChamp(vs)?.name}` : ""}</h2>
            <div className="row" style={{gap:18,marginTop:12,fontSize:13}}>
              <span><strong className="t-win mono" style={{fontSize:16}}>{b.winrate}%</strong> winrate</span>
              <span className="muted">·</span>
              <span><strong className="mono" style={{fontSize:16}}>{b.games.toLocaleString("fr-FR")}</strong> parties</span>
              <span className="muted">·</span>
              <span>Patch <strong className="t-accent">{ddVersion()}</strong></span>
            </div>
          </div>
        </div>
      </div>

      <div className="g-3" style={{marginBottom:24,alignItems:"start"}}>
        <div className="card">
          <SecHead title="Runes" meta={b.runes?.perks ? "page la plus jouée" : "styles dominants"}/>
          {primary || sub ? (
            <div className="stack-sm">
              {primary && <div className="row-tight" style={{gap:12}}><img src={runeStyleIcon(primary.icon)} alt="" style={{width:32,height:32}}/><div><div style={{fontWeight:700}}>{primary.name}</div><div className="muted" style={{fontSize:12}}>Voie principale</div></div></div>}
              {b.runes?.perks && (
                <div className="row" style={{gap:10,flexWrap:"wrap",margin:"10px 0 4px"}}>
                  {b.runes.perks.slice(0, 4).map((id) => {
                    const p = getRunePerk(id);
                    if (!p) return null;
                    return (
                      <div key={id} className="rune-cell" title={p.name}>
                        <img src={runeStyleIcon(p.icon)} alt={p.name} style={{width:p.keystone?44:32,height:p.keystone?44:32,margin:"0 auto"}}/>
                        <div className="rune-lbl">{p.name}</div>
                      </div>
                    );
                  })}
                </div>
              )}
              {sub && <div className="row-tight" style={{gap:12,marginTop:6}}><img src={runeStyleIcon(sub.icon)} alt="" style={{width:26,height:26}}/><div><div style={{fontWeight:600,fontSize:13}}>{sub.name}</div><div className="muted" style={{fontSize:12}}>Voie secondaire</div></div></div>}
              {b.runes?.perks && b.runes.perks.length > 4 && (
                <div className="row" style={{gap:10,marginTop:6}}>
                  {b.runes.perks.slice(4).map((id) => {
                    const p = getRunePerk(id);
                    if (!p) return null;
                    return (
                      <div key={id} className="rune-cell" title={p.name}>
                        <img src={runeStyleIcon(p.icon)} alt={p.name} style={{width:30,height:30,margin:"0 auto"}}/>
                        <div className="rune-lbl">{p.name}</div>
                      </div>
                    );
                  })}
                </div>
              )}
              {b.runes?.statShards && (
                <div className="row" style={{gap:8,marginTop:10,alignItems:"center"}}>
                  <span className="muted" style={{fontSize:11}}>Shards</span>
                  {b.runes.statShards.map((id, i) => {
                    const s = getStatShard(id);
                    if (!s) return null;
                    return <img key={i} src={runeStyleIcon(s.icon)} alt={s.name} title={s.name} style={{width:22,height:22,background:"var(--surface-2)",borderRadius:"50%",padding:2}}/>;
                  })}
                </div>
              )}
            </div>
          ) : <EmptyState title="—"/>}
          {!b.runes?.perks && <p className="muted" style={{fontSize:12,marginTop:14,marginBottom:0}}>Page complète bientôt disponible (les runes détaillées sont collectées depuis juin 2026).</p>}
        </div>

        <div className="card">
          <SecHead title="Sorts d'invocateur" meta="paire la plus jouée"/>
          <div className="row" style={{gap:24,marginBottom:8,justifyContent:"center",paddingTop:8}}>
            {(b.spells || []).map((id,i) => <div key={i} style={{textAlign:"center"}}><SpellById id={id} size={52}/></div>)}
            {!b.spells?.length && <span className="muted">—</span>}
          </div>
        </div>

        <BootsCard b={b}/>
      </div>

      {b.skillOrder?.length > 0 && (
        <div className="card" style={{marginBottom:24}}>
          <SecHead title="Ordre de sorts" meta="parties gagnantes récentes"/>
          <SkillOrderStrip order={b.skillOrder}/>
        </div>
      )}

      <BuildItemsCard b={b}/>
    </div>
  );
}

// ---- Bottes, à part : options avec winrate + moment d'achat habituel ----
const BOOTS_TIMING = ["avant le premier objet", "après le 1er objet", "après le 2e objet", "après le 3e objet"];
function BootsCard({ b }) {
  const opts = b.items.bootsOptions?.length ? b.items.bootsOptions
    : (b.items.boots ? [{ id: b.items.boots, games: null, winrate: null, pct: null }] : []);
  const at = b.items.bootsAt;
  return (
    <div className="card">
      <SecHead title="Bottes" meta={at != null ? `Achetées en général ${BOOTS_TIMING[Math.min(3, at)]}` : "choix le plus fréquent"}/>
      {opts.length ? (
        <div className="stack-sm">
          {opts.map((o, i) => (
            <div key={o.id} className="row-tight" style={{gap:12, padding:"6px 0", borderTop: i ? "1px solid var(--border)" : "none"}}>
              <ItemImg id={o.id} size={i === 0 ? 48 : 40}/>
              <div className="grow">
                <div style={{fontWeight:700,fontSize:14}}>{getItem(o.id)?.name || "Bottes"}</div>
                <div className="muted" style={{fontSize:12}}>
                  {o.pct != null ? `${o.pct}% des joueurs` : "Choix le plus fréquent"}{o.games ? ` · ${o.games.toLocaleString("fr-FR")} parties` : ""}
                </div>
              </div>
              {o.winrate != null && <span className="mono" style={{fontWeight:800,fontSize:14,color: o.winrate >= 50 ? "var(--win)" : "var(--loss)"}}>{o.winrate}% WR</span>}
            </div>
          ))}
          {opts[0]?.source === 'timeline' && <p className="muted" style={{fontSize:11,margin:"8px 0 0"}}>Relevé sur les achats en cours de partie : ce rôle revend souvent ses bottes en fin de partie.</p>}
        </div>
      ) : <EmptyState title="Pas de bottes dominantes" hint="Aucune paire de bottes ne ressort dans les parties analysées."/>}
    </div>
  );
}

// ---- Objets : départ, builds conseillés (ordre d'achat) et situationnels ----
function ItemNode({ id, index, dim, small }) {
  const it = getItem(id);
  return (
    <div className="bp-node" style={{opacity: dim ? 0.7 : 1}} title={it?.name || `#${id}`}>
      <div className="bp-img"><ItemImg id={id} size={small ? 40 : 52}/>{index != null && <span className="bp-idx">{index}</span>}</div>
      <div className="bp-lbl">{it?.name || `#${id}`}</div>
    </div>
  );
}
function StartNode({ items }) {
  const counts = new Map();
  items.forEach((id) => counts.set(id, (counts.get(id) || 0) + 1));
  return (
    <div className="bp-node bp-start" title="Achats de départ">
      <div className="row-tight" style={{gap:4,justifyContent:"center"}}>
        {[...counts.entries()].map(([id, n]) => (
          <div key={id} className="bp-img" style={{position:"relative"}}><ItemImg id={id} size={34}/>{n > 1 && <span className="bp-idx" style={{background:"var(--surface-3)",color:"var(--ink)"}}>×{n}</span>}</div>
        ))}
      </div>
      <div className="bp-lbl">Départ</div>
    </div>
  );
}
const Arrow = () => <span className="bp-arrow"><Icon name="chevR" size={14}/></span>;

function BuildVariant({ v, idx, starting }) {
  const nodes = [];
  if (starting?.items?.length) nodes.push(<StartNode key="start" items={starting.items}/>);
  v.core.forEach((id, i) => nodes.push(<ItemNode key={id} id={id} index={i + 1}/>));
  const rest = v.full.slice(v.core.length);
  return (
    <div className="bp-variant">
      <div className="row-bw" style={{flexWrap:"wrap",gap:10,marginBottom:12}}>
        <div className="row-tight" style={{gap:10}}>
          <span className="tag solid" style={{whiteSpace:"nowrap"}}>Build {idx + 1}</span>
          <span className="mono" style={{fontWeight:800,fontSize:16,whiteSpace:"nowrap",color: v.winrate >= 50 ? "var(--win)" : "var(--loss)"}}>{v.winrate}% WR</span>
          <span className="muted" style={{fontSize:12}}>{v.games.toLocaleString("fr-FR")} parties · {v.pct}% des joueurs</span>
        </div>
        <span className="muted" style={{fontSize:11}}>{v.orderSource === 'timeline' ? "Ordre d'achat observé dans les parties récentes" : "Ordre par fréquence (achats non observés)"}</span>
      </div>
      <div className="bp-path">
        {nodes.map((n, i) => <React.Fragment key={i}>{i > 0 && <Arrow/>}{n}</React.Fragment>)}
        {rest.length > 0 && (
          <>
            <span className="bp-sep">puis</span>
            {rest.map((id, i) => <React.Fragment key={id}>{i > 0 && <Arrow/>}<ItemNode id={id} index={v.core.length + i + 1} dim/></React.Fragment>)}
          </>
        )}
      </div>
    </div>
  );
}

function BuildItemsCard({ b }) {
  const builds = b.items.builds || [];
  const starting = b.items.starting?.[0] || null;
  const sample = b.items.sample || {};
  return (
    <div className="card">
      <SecHead title="Builds conseillés"
               meta={builds.length ? `${builds.length} variante${builds.length > 1 ? "s" : ""} · winrates sur ${(sample.rows || 0).toLocaleString("fr-FR")} parties récentes${sample.timelines ? ` · ordre d'achat relevé sur ${sample.timelines} parties` : ""}` : "objets les plus construits"}/>
      {b.items.starting?.length > 0 && (
        <div className="row" style={{gap:16,alignItems:"center",marginBottom:18,flexWrap:"wrap"}}>
          <span className="muted" style={{fontSize:12,fontWeight:600,letterSpacing:"0.06em",textTransform:"uppercase"}}>Départ</span>
          {b.items.starting.map((st, i) => (
            <div key={i} className="row-tight" style={{gap:8,background:"var(--surface-2)",padding:"6px 10px",borderRadius:10}}>
              {[...new Map(st.items.map((id) => [id, st.items.filter((x) => x === id).length])).entries()].map(([id, n]) => (
                <div key={id} className="row-tight" style={{gap:4}}><ItemImg id={id} size={28}/>{n > 1 && <span className="mono muted" style={{fontSize:11}}>×{n}</span>}</div>
              ))}
              <span className="muted" style={{fontSize:11}}>{st.pct}%</span>
            </div>
          ))}
        </div>
      )}
      {builds.length ? (
        <div className="stack">{builds.map((v, i) => <BuildVariant key={i} v={v} idx={i} starting={starting}/>)}</div>
      ) : (
        <>
          <div className="muted" style={{fontSize:12,fontWeight:600,margin:"6px 0 14px",letterSpacing:"0.06em",textTransform:"uppercase"}}>Build complet</div>
          <div className="row" style={{gap:14, flexWrap:"wrap"}}>
            {(b.items.full || []).map((id, i) => <ItemNode key={i} id={id} index={i + 1}/>)}
          </div>
        </>
      )}

      {b.items.situational?.length > 0 && <>
        <div className="divider"/>
        <div className="muted" style={{fontSize:12,fontWeight:600,marginBottom:14,letterSpacing:"0.06em",textTransform:"uppercase"}}>Objets situationnels</div>
        <div className="row" style={{gap:12,flexWrap:"wrap"}}>
          {b.items.situational.map((id,i) => (
            <div key={i} className="row-tight" style={{gap:8,background:"var(--surface-2)",padding:"8px 12px",borderRadius:10}}>
              <ItemImg id={id} size={32}/><span style={{fontSize:13}}>{getItem(id)?.name || `#${id}`}</span>
            </div>
          ))}
        </div>
      </>}
      <style>{`
        .bp-variant { padding: 16px; border: 1px solid var(--border); border-radius: 14px; background: var(--surface-2); }
        .bp-path { display: flex; align-items: flex-start; gap: 6px; flex-wrap: wrap; }
        .bp-node { display: flex; flex-direction: column; align-items: center; width: 84px; text-align: center; }
        .bp-start { width: auto; min-width: 84px; }
        .bp-img { position: relative; display: inline-block; }
        .bp-idx { position: absolute; left: -6px; top: -6px; width: 20px; height: 20px; border-radius: 6px; background: var(--accent); color: var(--accent-text);
                  font-family: var(--font-display); font-weight: 800; font-size: 11px; display: inline-flex; align-items: center; justify-content: center; }
        .bp-lbl { font-size: 10.5px; line-height: 1.2; color: var(--ink-3); margin-top: 6px; overflow-wrap: anywhere; }
        .bp-arrow { color: var(--ink-mute); display: inline-flex; align-items: center; height: 52px; }
        .bp-sep { font-size: 11px; color: var(--ink-3); font-weight: 600; display: inline-flex; align-items: center; height: 52px; padding: 0 6px; letter-spacing: .04em; text-transform: uppercase; }
        @media (max-width: 640px) {
          .bp-variant { padding: 12px; }
          .bp-node { width: 62px; }
          .bp-start { min-width: 62px; }
          .bp-img .item { width: 40px !important; height: 40px !important; }
          .bp-lbl { font-size: 9.5px; }
          .bp-arrow, .bp-sep { height: 40px; }
          .bp-path { gap: 4px; }
        }
      `}</style>
    </div>
  );
}

export {
  ChampionsScreen, ChampionDetailScreen, BuildScreen, BuildSheet,
};
